import { faCircleQuestion } from "@fortawesome/sharp-solid-svg-icons";
import { Box, useTheme } from "@mui/material";
import { format } from "date-fns";
import { useAtomValue } from "jotai";
import { HTMLAttributes, useMemo } from "react";
import { editMode } from "../../../atoms/profileScreenEdit";
import EntityPhoto from "../../../store/models/entityPhoto";
import {
  isLive,
  isScheduled,
  resolvePublishDate,
} from "../../../utils/releases/dateUtils";
import { Button, ButtonVariant } from "../../core-ui/components/Button/Button";
import { Text, TEXT_WEIGHT } from "../../core-ui/components/Text/Text";
import {
  TextColor,
  TextStyleVariant,
} from "../../core-ui/components/Text/TextUtils";
import { getEntityPhotoImageProps } from "../../screens/ProfileScreen/utils";
import { FeatureReleasePill } from "../FeaturedArtistRelease/FeaturedReleasePill";
import { ServiceCardQuestionMarkIcon } from "../ServiceCard/ServiceCardV2/ServiceCardV2.styles";
import {
  PriceTag,
  PriceTagContainer,
  ProductBodyContainer,
  ProductCardContainer,
  ProductFooter,
  ProductImage,
} from "./ProductCard.styles";

export interface ProductCardProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onClick"> {
  coverPhoto: EntityPhoto | null;
  name: string;
  description: string;
  totalPrice: string;
  onShowMore: () => void;
  releaseId: number;
  productPurchasedByCurrentUser: boolean;
  availableQuantity?: null | number;
  productId: number;
  purchaseLimit?: null | number;
  publishedAt: string | null;
  publishedEnd: string | null;
  releasePublishedAt: string | null;
  releasePublishedEnd: string | null;
}

export const ProductCard = ({
  name,
  description,
  totalPrice,
  onShowMore,
  coverPhoto,
  publishedAt,
  publishedEnd,
  releasePublishedAt,
  releasePublishedEnd,
  ...extraProps
}: ProductCardProps) => {
  const displayingDescription = useMemo(() => {
    // For the Product Card, we only render the first line of description
    // In addition, the maximum words to be displayed is 8, so that it doesn't span to more than 2 lines
    const res = description ? description.split("\n")[0].slice(0, 65) : "";
    return res === description ? description : `${res}...`;
  }, [description]);
  const isEditMode = useAtomValue(editMode);
  const { publishedDate, scheduled, live } = useMemo(() => {
    const dates = {
      publishedAt,
      publishedEnd,
      releasePublishedAt,
      releasePublishedEnd,
    };
    return {
      publishedDate: resolvePublishDate(dates),
      scheduled: isScheduled(dates),
      live: isLive(dates),
    };
  }, [publishedAt, publishedEnd, releasePublishedAt, releasePublishedEnd]);

  const theme = useTheme();

  const productStatusColor = useMemo(() => {
    if (scheduled) {
      return theme.palette.pending;
    }
    return theme.palette.active;
  }, [scheduled, theme.palette.active, theme.palette.pending]);

  return (
    <ProductCardContainer
      onClick={onShowMore}
      {...extraProps}
      className="product-card"
    >
      <ProductImage
        sx={{
          opacity: scheduled ? 0.5 : 1,
          cursor: scheduled ? "not-allowed" : "pointer",
          pointerEvents: scheduled ? "none" : "auto",
        }}
        {...getEntityPhotoImageProps(coverPhoto)}
      />
      {/* Always show scheduled date - only show live if in edit mode */}
      {((isEditMode && live) || scheduled) && (
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            height: 50,
            width: "fit-content",
          }}
        >
          <FeatureReleasePill
            text={
              live
                ? "Live Now"
                : publishedDate
                  ? format(publishedDate, "MM/dd/yyyy hh:mm a")
                  : "Scheduled"
            }
            color={productStatusColor}
          />
        </Box>
      )}
      <ProductBodyContainer
        sx={{
          opacity: scheduled ? 0.5 : 1,
          cursor: scheduled ? "not-allowed" : "pointer",
          pointerEvents: scheduled ? "none" : "auto",
        }}
      >
        <Box sx={{ minHeight: "54px" }}>
          <Text variant={TextStyleVariant.S3}>{name}</Text>
          <Text variant={TextStyleVariant.P3} color={TextColor.SECONDARY}>
            {displayingDescription}
          </Text>
        </Box>
        <ProductFooter>
          <PriceTagContainer>
            <PriceTag
              variant={TextStyleVariant.P2}
              weight={TEXT_WEIGHT.SEMI_BOLD}
              style={{ lineHeight: 1 }}
            >
              {totalPrice}
            </PriceTag>
          </PriceTagContainer>
          <Button
            style={{ minHeight: "unset" }}
            variant={ButtonVariant.UNSTYLED}
          >
            <Box
              sx={{
                display: "flex",
                gap: "4px",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Text
                variant={TextStyleVariant.P2}
                weight={TEXT_WEIGHT.SEMI_BOLD}
              >
                What&apos;s Included
              </Text>
              <ServiceCardQuestionMarkIcon
                sx={{ fontSize: "12px" }}
                icon={faCircleQuestion}
              />
            </Box>
          </Button>
        </ProductFooter>
      </ProductBodyContainer>
    </ProductCardContainer>
  );
};
